import { DateTime } from "luxon";
import { createSelector } from "@reduxjs/toolkit";

import Filters from "../../../../filters";
import Account from "../../../../Account";
import { createDeepEqualSelector } from "../utils/createDeepEqualSelector";
import { selectAllAccounts } from "../accounts";

import { DisplayStatus } from "./consts";

import type { RootState } from "../store";
import type { FileImportItem, FormattedFileImportItem } from "./types";

// Basic selectors
export const selectFileImportActivityState = (state: RootState) =>
  state.fileImportActivity;

export const selectFileImportActivityById = createSelector(
  [selectFileImportActivityState],
  (s) => s.byId,
);

export const selectFileImportActivityPageMap = createSelector(
  [selectFileImportActivityState],
  (s) => s.pageMap,
);

export const selectFileImportActivityTotal = createSelector(
  [selectFileImportActivityState],
  (s) => s.total,
);

// Formatting helper
const formatFileImportItem = (
  item: FileImportItem,
  accounts: API.AnyAccount[],
): FormattedFileImportItem => {
  const account = accounts.find((a) => a.id === item.from_account);
  const formattedAccount = account ? Account.deserialize(account) : null;

  const state = item.state ?? "unknown";
  const displayStatus = DisplayStatus[state] || DisplayStatus.unknown;

  const amountCents = item.total_amount as Cents;
  const amountDollars = Filters.currency(amountCents, {
    hasDecimal: false,
  });
  const netTotalDollarsWithSign =
    amountCents > 0 ? `+${amountDollars}` : amountDollars;
  const organizationFile = item.file_name
    ? ({
        file_name: item.file_name,
        file_type: "csv",
      } as API.BulkPayment["organization_file"])
    : null;

  const dateRange = item.created_at
    ? DateTime.fromISO(item.created_at).toFormat("MMM d, yyyy")
    : "";

  return {
    uuid: item.id,
    type: item.type,
    title: item.title,
    state,
    displayStatus,
    institutionAccount: formattedAccount,
    organizationFile,
    netTotalDollars: amountDollars,
    netTotalDollarsWithSign,
    dateRange,
    paymentCount: item.payment_count,
    paymentCreditCount: item.payment_credit_count,
    paymentDebitCount: item.payment_debit_count,
    isExpedited: false,
    dualApprovalId: item.dual_approval_id,
    userId: item.user_id,
    fileName: item.file_name,
    isReversible: item.is_reversible,
    isCancellable: item.is_cancellable,
    hasReversal: item.bulk_reversals.length > 0,
    batchCount: item.batch_count,
    originatingAccountCount: item.originating_account_count,
    balanceClassification: item.balance_classification,
  };
};

// Computed selector: formatted items for a given page
export const selectFormattedPaginatedFileImports = createDeepEqualSelector(
  [
    selectFileImportActivityPageMap,
    selectFileImportActivityById,
    selectAllAccounts,
    (_, pageNumber: number) => pageNumber,
  ],
  (pageMap, byId, accounts, pageNumber) => {
    const ids = pageMap[pageNumber] ?? [];
    return ids
      .map((id) => {
        const item = byId[id];
        if (!item) return null;
        return formatFileImportItem(item, accounts);
      })
      .filter(Boolean) as FormattedFileImportItem[];
  },
);

// Computed selector: formatted items for infinite scroll on mobile
export const selectFormattedFileImportsThroughPage = createDeepEqualSelector(
  [
    selectFileImportActivityPageMap,
    selectFileImportActivityById,
    selectAllAccounts,
    (_, pageNumber: number) => pageNumber,
  ],
  (pageMap, byId, accounts, pageNumber) => {
    const ids = Array.from({ length: Math.max(pageNumber, 0) + 1 }).flatMap(
      (_, idx) => pageMap[idx] ?? [],
    );

    return ids
      .map((id) => {
        const item = byId[id];
        if (!item) return null;
        return formatFileImportItem(item, accounts);
      })
      .filter(Boolean) as FormattedFileImportItem[];
  },
);
